import { useState } from 'react';
import { AxiosError } from 'axios';
import { getErrorMessageFromAxiosError } from '../../request/utils';
import { useDontMountAtFirst } from '../../shared/hooks/useDontMountAtFirst';

export function useConfirmModal<D extends object>({
  open,
  onPositiveClick,
  onPositiveClickCallback,
  onPositiveClickSuccess,
  onClose,
  hideActionOnError
}: {
  open?: boolean;
  onPositiveClick?: (extraParams?: D) => void;
  onPositiveClickCallback?: (extraParams?: D) => Promise<unknown>;
  onPositiveClickSuccess?: (data: unknown) => void;
  onClose: (data?: unknown) => void;
  hideActionOnError?: (error: AxiosError) => boolean;
}) {
  const [extraParams, setExtraParams] = useState<D>();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<AxiosError | undefined>();
  const [isActionHiddenOnError, setIsActionHiddenOnError] = useState(false);
  const onErrorMessage = error ? getErrorMessageFromAxiosError(error) : undefined;

  const onPositiveButtonClick = async () => {
    setError(undefined);

    if (typeof onPositiveClick === 'function') {
      onPositiveClick(extraParams);
    }

    setProcessing(true);

    try {
      let response: boolean | unknown = true;

      if (typeof onPositiveClickCallback === 'function') {
        response = await onPositiveClickCallback(extraParams);
      }

      setProcessing(false);

      if (typeof onPositiveClickSuccess === 'function') {
        onPositiveClickSuccess(response);
      } else {
        close(response);
      }
    } catch (e) {
      const axiosError = e as AxiosError;

      setProcessing(false);
      setError(axiosError);

      if (typeof hideActionOnError === 'function' && hideActionOnError(axiosError)) {
        setIsActionHiddenOnError(true);
      }
    }
  };

  const close = (params?: boolean | unknown) => {
    onClose(params ?? extraParams);
  };

  useDontMountAtFirst(() => {
    if (!open) {
      setProcessing(false);
      setError(undefined);
      setExtraParams(undefined);
      setIsActionHiddenOnError(false);
    }
  }, [open]);

  return {
    isActionHiddenOnError,
    extraParams,
    processing,
    error,
    onErrorMessage,
    onPositiveButtonClick,
    setExtraParams,
    setError,
    close
  };
}
